import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";

export default function Privacidad() {
  return (
    <>
      <SEO 
        title="Política de Privacidad | WPRescate24"
        description="Política de privacidad y protección de datos personales de WPRescate24. Conoce cómo protegemos tu información."
        canonicalUrl="https://wprescate24.com/privacidad"
      />
      <div className="dark min-h-screen bg-background">
        {/* Header Simple */}
        <header className="sticky top-0 z-50 backdrop-blur-sm bg-white/95 border-b border-border shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <RouterLink to="/" className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" />
              <span className="font-bold text-foreground text-lg">WPRescate24</span>
            </RouterLink>
            <Button variant="outline" asChild>
              <RouterLink to="/" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Volver al inicio
              </RouterLink>
            </Button>
          </div>
        </header>

        {/* Contenido */}
        <main className="max-w-4xl mx-auto px-6 py-16">
          <h1 className="text-4xl font-bold text-foreground mb-8">Política de Privacidad</h1>
          <p className="text-foreground/70 mb-8">
            <strong>Última actualización:</strong> {new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <div className="prose prose-lg max-w-none space-y-8">
            {/* Introducción */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">Introducción</h2>
              <p className="text-foreground/80 leading-relaxed">
                En WPRescate24, operado por GAREY AW, nos comprometemos a proteger la privacidad y seguridad de la información personal de nuestros clientes y visitantes. Esta Política de Privacidad explica cómo recopilamos, usamos, compartimos y protegemos su información personal de acuerdo con las leyes aplicables de protección de datos en México.
              </p>
            </section>

            {/* 1. Información que Recopilamos */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">1. Información que Recopilamos</h2>
              
              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">1.1 Información Proporcionada Directamente</h3>
              <p className="text-foreground/80 leading-relaxed mb-4">
                Cuando solicita nuestros servicios, podemos recopilar:
              </p>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                <li>Nombre completo</li>
                <li>Dirección de correo electrónico</li>
                <li>Número de teléfono</li>
                <li>URL de su sitio web</li>
                <li>Información sobre su problema técnico o necesidad</li>
                <li>Credenciales de acceso a su sitio web y hosting (solo cuando nos autoriza)</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">1.2 Información Técnica Recopilada Automáticamente</h3>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                <li>Dirección IP</li>
                <li>Tipo de navegador y versión</li>
                <li>Páginas visitadas en nuestro sitio</li>
                <li>Fecha y hora de las visitas</li>
                <li>Datos de Google Analytics (anónimos)</li>
              </ul>
            </section>

            {/* 2. Cómo Usamos su Información */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">2. Cómo Usamos su Información</h2>
              <p className="text-foreground/80 leading-relaxed mb-4">
                Utilizamos la información recopilada para:
              </p>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                <li>Prestar los servicios de rescate, mantenimiento y optimización solicitados</li>
                <li>Comunicarnos con usted sobre su solicitud o servicio contratado</li>
                <li>Enviar actualizaciones sobre el estado de su sitio web</li>
                <li>Procesar pagos y facturación</li>
                <li>Mejorar nuestros servicios y experiencia del usuario</li>
                <li>Cumplir con obligaciones legales y fiscales</li>
                <li>Prevenir fraudes y garantizar la seguridad de nuestros sistemas</li>
              </ul>
            </section>

            {/* 3. Base Legal para el Procesamiento */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">3. Base Legal para el Procesamiento de Datos</h2>
              <p className="text-foreground/80 leading-relaxed mb-4">
                Procesamos sus datos personales bajo las siguientes bases legales:
              </p>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                <li><strong>Consentimiento:</strong> Al utilizar nuestros formularios de contacto, usted nos otorga su consentimiento expreso</li>
                <li><strong>Ejecución de contrato:</strong> Para prestar los servicios que ha solicitado</li>
                <li><strong>Interés legítimo:</strong> Para mejorar nuestros servicios y comunicaciones</li>
                <li><strong>Obligaciones legales:</strong> Para cumplir con requisitos fiscales y legales</li>
              </ul>
            </section>

            {/* 4. Compartir Información */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">4. Compartir su Información</h2>
              <p className="text-foreground/80 leading-relaxed mb-4">
                No vendemos ni alquilamos su información personal a terceros. Podemos compartir información solo en los siguientes casos:
              </p>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                <li><strong>Proveedores de servicios:</strong> Empresas que nos ayudan a prestar servicios (ej: hosting, procesamiento de pagos, envío de emails) bajo estrictos acuerdos de confidencialidad</li>
                <li><strong>Cumplimiento legal:</strong> Cuando sea requerido por ley, orden judicial o autoridades gubernamentales</li>
                <li><strong>Protección de derechos:</strong> Para proteger nuestros derechos legales, propiedad o seguridad</li>
              </ul>
            </section>

            {/* 5. Cookies y Tecnologías Similares */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">5. Cookies y Tecnologías de Seguimiento</h2>
              <p className="text-foreground/80 leading-relaxed mb-4">
                Utilizamos cookies y tecnologías similares para:
              </p>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                <li><strong>Cookies esenciales:</strong> Necesarias para el funcionamiento del sitio</li>
                <li><strong>Cookies analíticas:</strong> Google Analytics para entender cómo los visitantes usan nuestro sitio (datos anónimos)</li>
                <li><strong>Cookies de preferencias:</strong> Para recordar sus configuraciones</li>
              </ul>
              <p className="text-foreground/80 leading-relaxed mt-4">
                Puede configurar su navegador para rechazar cookies, aunque esto puede afectar la funcionalidad del sitio.
              </p>
            </section>

            {/* 6. Seguridad de Datos */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">6. Seguridad de sus Datos</h2>
              <p className="text-foreground/80 leading-relaxed mb-4">
                Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger su información:
              </p>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                <li>Cifrado SSL/TLS en todas las comunicaciones</li>
                <li>Almacenamiento seguro de credenciales de acceso</li>
                <li>Acceso restringido a datos personales solo para personal autorizado</li>
                <li>Monitoreo continuo de seguridad</li>
                <li>Backups regulares y seguros</li>
                <li>Auditorías periódicas de seguridad</li>
              </ul>
              <p className="text-foreground/80 leading-relaxed mt-4">
                Sin embargo, ningún método de transmisión por Internet es 100% seguro. Nos esforzamos por proteger su información, pero no podemos garantizar su seguridad absoluta.
              </p>
            </section>

            {/* 7. Retención de Datos */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">7. Retención de Datos</h2>
              <p className="text-foreground/80 leading-relaxed">
                Conservamos su información personal solo durante el tiempo necesario para cumplir con los fines descritos en esta política, a menos que la ley requiera o permita un período de retención más largo. Generalmente:
              </p>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4 mt-4">
                <li><strong>Información de contacto:</strong> Mientras mantengamos relación comercial o hasta que solicite su eliminación</li>
                <li><strong>Información de facturación:</strong> 5 años (requerimiento fiscal en México)</li>
                <li><strong>Credenciales de acceso:</strong> Solo durante la prestación activa del servicio</li>
                <li><strong>Datos analíticos:</strong> Hasta 26 meses (Google Analytics)</li>
              </ul>
            </section>

            {/* 8. Sus Derechos ARCO */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">8. Sus Derechos (ARCO)</h2>
              <p className="text-foreground/80 leading-relaxed mb-4">
                De acuerdo con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares, usted tiene derecho a:
              </p>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                <li><strong>Acceso:</strong> Conocer qué datos personales tenemos sobre usted</li>
                <li><strong>Rectificación:</strong> Solicitar la corrección de datos inexactos o incompletos</li>
                <li><strong>Cancelación:</strong> Solicitar la eliminación de sus datos personales</li>
                <li><strong>Oposición:</strong> Oponerse al tratamiento de sus datos para fines específicos</li>
                <li><strong>Revocar consentimiento:</strong> Retirar su consentimiento en cualquier momento</li>
                <li><strong>Limitar uso:</strong> Limitar el uso o divulgación de sus datos</li>
              </ul>
              <p className="text-foreground/80 leading-relaxed mt-4">
                Para ejercer estos derechos, contáctenos a <a href="mailto:privacidad@wprescate24.com" className="text-primary hover:underline">privacidad@wprescate24.com</a>
              </p>
            </section>

            {/* 9. Transferencias Internacionales */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">9. Transferencias Internacionales de Datos</h2>
              <p className="text-foreground/80 leading-relaxed">
                Algunos de nuestros proveedores de servicios pueden estar ubicados fuera de México (ej: Google Analytics, servicios de hosting en la nube). En estos casos, nos aseguramos de que existan medidas de protección adecuadas mediante cláusulas contractuales estándar o certificaciones de privacidad reconocidas.
              </p>
            </section>

            {/* 10. Menores de Edad */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">10. Privacidad de Menores</h2>
              <p className="text-foreground/80 leading-relaxed">
                Nuestros servicios no están dirigidos a menores de 18 años. No recopilamos intencionalmente información personal de menores. Si descubrimos que hemos recopilado datos de un menor sin consentimiento parental, eliminaremos esa información inmediatamente.
              </p>
            </section>

            {/* 11. Cambios a esta Política */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">11. Cambios a esta Política</h2>
              <p className="text-foreground/80 leading-relaxed">
                Nos reservamos el derecho de modificar esta Política de Privacidad en cualquier momento. Las modificaciones serán efectivas inmediatamente después de su publicación en esta página. Le notificaremos sobre cambios significativos por correo electrónico o mediante un aviso destacado en nuestro sitio web.
              </p>
            </section>

            {/* 12. Servicios de Terceros */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">12. Enlaces a Sitios de Terceros</h2>
              <p className="text-foreground/80 leading-relaxed">
                Nuestro sitio puede contener enlaces a sitios web de terceros. No somos responsables de las prácticas de privacidad de estos sitios. Le recomendamos leer las políticas de privacidad de cualquier sitio que visite.
              </p>
            </section>

            {/* 13. Contacto */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">13. Contacto para Asuntos de Privacidad</h2>
              <p className="text-foreground/80 leading-relaxed mb-4">
                Para preguntas, inquietudes o ejercer sus derechos ARCO, contáctenos:
              </p>
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                <p className="text-foreground/80 mb-2"><strong>WPRescate24 - Departamento de Privacidad</strong></p>
                <p className="text-foreground/80 mb-2">Una empresa de GAREY AW</p>
                <p className="text-foreground/80 mb-2">Email: <a href="mailto:privacidad@wprescate24.com" className="text-primary hover:underline">privacidad@wprescate24.com</a></p>
                <p className="text-foreground/80 mb-2">Email general: <a href="mailto:ventas@wprescate24.com" className="text-primary hover:underline">ventas@wprescate24.com</a></p>
                <p className="text-foreground/80 mb-2">WhatsApp: <a href="https://wa.me/527223818617" className="text-primary hover:underline">+52 722 381 8617</a></p>
                <p className="text-foreground/80">Tiempo de respuesta: 20 días hábiles (conforme a ley)</p>
              </div>
            </section>

            {/* 14. Autoridad de Protección de Datos */}
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">14. Autoridad de Protección de Datos</h2>
              <p className="text-foreground/80 leading-relaxed">
                Si considera que sus derechos de protección de datos han sido vulnerados, puede presentar una queja ante el Instituto Nacional de Transparencia, Acceso a la Información y Protección de Datos Personales (INAI):
              </p>
              <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4 mt-4">
                <li>Sitio web: <a href="https://home.inai.org.mx" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">home.inai.org.mx</a></li>
                <li>Teléfono: 800 835 4324</li>
              </ul>
            </section>
          </div>

          {/* Botón Volver */}
          <div className="mt-12 pt-8 border-t border-border text-center">
            <Button asChild className="bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-6">
              <RouterLink to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver al Inicio
              </RouterLink>
            </Button>
          </div>
        </main>

        {/* Footer Simple */}
        <footer className="border-t border-border bg-background py-8">
          <div className="max-w-7xl mx-auto px-6 text-center text-sm text-foreground/60">
            <p>© {new Date().getFullYear()} WPRescate24. Todos los derechos reservados. Una empresa de <a href="https://garey.mx" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-semibold">GAREY AW</a></p>
          </div>
        </footer>
      </div>
    </>
  );
}
